import React from 'react';

import Button from '@mui/material/Button';
import Stack, { type StackProps } from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';

import { type BillScoutSetupEntrySource, useBillScoutSetupState } from 'app-components/hooks/billScoutSetup';

import { useLaunchBillScoutSetup } from 'components/BillScoutSetup/useLaunchBillScoutSetup';
import SeriesIcon from 'images/icons/SeriesIcon';

import { useStyles } from './styles';

type BillScoutIntroProps = StackProps & {
  source: BillScoutSetupEntrySource;
  hasReminders?: boolean;
};

type CategorySummaryProps = {
  avatar: React.ReactNode;
  count: React.ReactNode;
  label: string;
  primaryVariant: TypographyProps['variant'];
  secondaryVariant: TypographyProps['variant'];
  listItemTextClassName?: string;
};

const CategorySummary = ({
  avatar,
  count,
  label,
  primaryVariant,
  secondaryVariant,
  listItemTextClassName,
}: CategorySummaryProps) => (
  <ListItem>
    <ListItemAvatar>{avatar}</ListItemAvatar>
    <ListItemText
      className={listItemTextClassName}
      primary={<Typography variant={primaryVariant}>{count}</Typography>}
      secondary={<Typography variant={secondaryVariant}>{label}</Typography>}
    />
  </ListItem>
);

const formatSuggestionCount = (size: number): number | '10+' => (size < 10 ? size : '10+');

const formatSuggestionLabel = (count: number | undefined, singular: string, plural: string): string =>
  count !== undefined && count > 1 ? plural : singular;

const BillScoutIntro = ({ source, hasReminders, ...rest }: BillScoutIntroProps) => {
  const { classes } = useStyles();
  const {
    hasInProgress,
    hasOnlyBills,
    hasOnlyIncome,
    showSetupSurfaces,
    isNewSetupEnabled,
    isControlArm,
    billCount,
    incomeCount,
  } = useBillScoutSetupState();
  const handleSetupClick = useLaunchBillScoutSetup(source);

  const handleCtaClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleSetupClick();
  };

  // Reqs Not Met never had a legacy modal of its own to fall back to on this
  // surface either (see isBillsNewSetupControlArm), so it stays on whatever
  // empty/populated state the caller renders instead of this component.
  if (!showSetupSurfaces || !(isNewSetupEnabled || isControlArm)) return null;

  const billsAvatar = <SeriesIcon kind='bill' shape='round' size='lg' />;
  const incomeAvatar = <SeriesIcon kind='income' shape='round' size='lg' />;

  const setupCta = (label: string, className: string) => (
    <Button variant='tonal' endIcon={<ArrowForward />} onClick={handleCtaClick} className={className}>
      {label}
    </Button>
  );

  const ctaLabel =
    hasInProgress && !(source === 'overviewTab' || source === 'allSeriesTab')
      ? 'Continue setup'
      : 'Set up bills and income';
  const billsSize = formatSuggestionCount(billCount);
  const incomeSize = formatSuggestionCount(incomeCount);

  const categoriesCard = (
    cardId: string,
    primaryVariant: TypographyProps['variant'],
    listItemTextClassName?: string,
  ) => (
    <Card id={cardId} className={classes.overviewCard}>
      <CardActionArea onClick={handleCtaClick}>
        <Box display='flex'>
          {billCount > 0 && (
            <CategorySummary
              avatar={billsAvatar}
              count={billsSize}
              label={formatSuggestionLabel(billCount, 'Bill', 'Bills')}
              primaryVariant={primaryVariant}
              secondaryVariant='paragraphRegular'
              listItemTextClassName={listItemTextClassName}
            />
          )}

          {billCount > 0 && incomeCount > 0 && <Divider flexItem orientation='vertical' />}

          {incomeCount > 0 && (
            <CategorySummary
              avatar={incomeAvatar}
              count={incomeSize}
              label={formatSuggestionLabel(incomeCount, 'Income source', 'Income sources')}
              primaryVariant={primaryVariant}
              secondaryVariant='paragraphRegular'
              listItemTextClassName={listItemTextClassName}
            />
          )}
        </Box>
      </CardActionArea>
    </Card>
  );

  if (source === 'overviewTab' || source === 'allSeriesTab') {
    const overviewHeading = hasOnlyBills
      ? 'Set up your bills'
      : hasOnlyIncome
        ? 'Set up your income'
        : 'Set up your bills and income';

    return (
      <Stack id={`scout-root-${source}`} alignItems='center' padding={2} gap={2} mb={4.5} {...rest}>
        {!hasReminders && (
          <Stack mt={2}>
            <Typography variant='bodyMedium'>{overviewHeading}</Typography>
          </Stack>
        )}

        {categoriesCard('scout-card-bill-and-income-overview', 'paragraphMedium', classes.compactListItemText)}

        <Stack alignItems='center'>
          <Typography variant='paragraphRegular' align='center' color='text.secondary'>
            Get set up with <b>Reminders</b> and projected balances in <b>Cash Flow</b>.
          </Typography>
        </Stack>

        {setupCta(ctaLabel, classes.setupButton)}
      </Stack>
    );
  }

  if (hasOnlyBills || hasOnlyIncome) {
    const count = hasOnlyBills ? billCount : incomeCount;
    const heading = hasOnlyBills ? 'We found potential bills' : 'We found potential income';
    const label = hasOnlyBills
      ? formatSuggestionLabel(count, 'Bill', 'Bills')
      : formatSuggestionLabel(count, 'Income source', 'Income sources');

    return (
      <Stack id='scout-root' padding={2} alignItems='center' gap={2} {...rest}>
        <Box>
          <Typography variant='bodyMedium'>{heading}</Typography>
        </Box>
        <Card className={classes.fullWidthCard} id='scout-card'>
          <CardActionArea className={classes.centeredCardActionArea} onClick={handleCtaClick}>
            <ListItem className={classes.fitContentListItem}>
              <ListItemAvatar className={classes.shrinkAvatar}>
                {hasOnlyBills ? billsAvatar : incomeAvatar}
              </ListItemAvatar>
              <ListItemText
                className={classes.headlineListItemText}
                primary={<Typography variant='headlineMedium'>{count ?? ''}</Typography>}
                secondary={<Typography variant='headlineRegular'>{label}</Typography>}
              />
            </ListItem>
          </CardActionArea>
        </Card>

        {setupCta('Set up', classes.introButton)}

        <Stack alignItems='center'>
          <Typography variant='paragraphRegular' align='center'>
            Set these up to get scheduled <b>Reminders</b> and to power <b>Cash Flow</b> with projected account
            balances.
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack id='scout-root' alignItems='center' padding={2} gap={2} {...rest}>
      <Typography variant='bodyMedium'>We found potential bills & income</Typography>

      {categoriesCard('scout-card', 'headlineMedium')}

      {setupCta(ctaLabel, classes.setupButton)}

      <Stack alignItems='center'>
        <Typography variant='bodyRegular' align='center'>
          Set these up to get scheduled <b>Reminders</b> and to power <b>Cash Flow</b> with projected account balances.
        </Typography>
      </Stack>
    </Stack>
  );
};

export default BillScoutIntro;
